import { v4 } from 'uuid';

import {
  CLI_DEFAULT_COMMAND_RESPONSE_TIMEOUT,
  CLI_DEFAULT_DATA_TIMEOUT,
  CLI_DEFAULT_LOGIN_TIMEOUT,
  CLI_DEFAULT_UNAUTHENTICATED_TIMEOUT,
} from '../constants';
import type { InternalSettings } from './types/types';

export const MAX_LOADED_SNAPSHOTS_FROM = 1;
export const MAX_LOADED_SNAPSHOTS_TO = 100;
// upper limit for max locked snapshots is current limit of max loaded snapshots - 1
export const MAX_LOCKED_SNAPSHOTS_FROM = 0;

export const DEFAULT_APP_SETTING: Omit<
  InternalSettings,
  'manualLinks' | 'pwdList'
> = {
  allowTelnet: true,
  bgp: [],
  customDiscoveryDefinitions: [],
  customerName: 'Test Customer',
  disabledPostDiscoveryActions: [],
  discoverySeedIpList: [],
  discoveryHistorySeeds: {
    enabled: true,
    daysLimit: null,
    afterDate: null,
  },
  discoveryTasks: [
    {
      family: null,
      id: v4(),
      label:
        'By default disabled for F5 - command is modifying the configuration and can break cluster synchronization',
      model: null,
      platform: null,
      sn: null,
      taskId: 'tasks/_init/disableTerminalPaging',
      vendor: 'f5',
      version: null,
    },
    {
      family: null,
      id: v4(),
      label:
        'By default disabled for all vendors - some devices can be affected by bug which can accidentally crash device.',
      model: null,
      platform: null,
      sn: null,
      taskId: 'tasks/transceivers',
      vendor: '.*',
      version: null,
    },
    {
      family: 'ftd',
      id: v4(),
      label:
        'By default disabled for Cisco Firepower - some versions may break discovery process',
      model: null,
      platform: null,
      sn: null,
      taskId: 'tasks/ntp',
      vendor: 'cisco',
      version: null,
    },
    {
      family: null,
      id: v4(),
      label:
        'By default disabled for Cisco - output could be huge that may break discovery process',
      model: null,
      platform: null,
      sn: null,
      taskId: 'tasks/bgpAdvertisedRoutes',
      vendor: 'cisco',
      version: null,
    },
  ],
  enablePwdList: [],
  backup: {
    enabled: false,
    cron: { expression: '0 23 * * *', timezone: 'UTC' },
    type: 'local',
  },
  cliRetryLimit: {
    authFail: 0,
    default: 3,
    tacacs: {
      delay: 1000,
      retry: 0,
    },
  },
  cliSessionsLimit: {
    enabled: false,
    limit: 200,
  },
  fullBgpIpv6Limit: {
    enabled: true,
    threshold: 500000,
  },
  fullBgpLimit: {
    enabled: true,
    threshold: 500000,
  },
  limitDiscoveryTasks: {
    alreadyDiscovered: false,
    sourceOfTasks: ['aciEndpoint', 'arp', 'ndpv6', 'routes', 'trace', 'xdp'],
  },
  maintenance: {
    enabled: true,
    cron: { expression: '0 0 * * *', timezone: 'UTC' },
  },
  networks: {
    exclude: [
      '0.0.0.0/8',
      '127.0.0.0/8',
      '224.0.0.0/4',
      '240.0.0.0/4',
      'fe80::/10',
      'ff00::/8',
      '::/128',
      '::1/128',
    ],
    include: ['0.0.0.0/0', '::/0'],
    whitelist: [],
  },
  resolveNames: {
    discoveryDevices: true,
  },
  snapshots: {
    cron: { expression: '0 * * * *', timezone: 'UTC' },
    enabled: false,
    maxLoaded: 3,
    maxLocked: 1,
  },
  snapshotsRetention: {
    all: {
      enabled: true,
      days: 30,
    },
    firstOfDay: {
      enabled: true,
      days: 120,
    },
    firstOfWeek: {
      enabled: true,
      weeks: 53,
    },
    firstOfMonth: {
      enabled: true,
      months: 60,
    },
    firstOfYear: {
      enabled: true,
      years: 24,
    },
    removalMetrics: {
      hddUtilization: 80,
      snapshotsCount: 1000,
    },
    action: {
      command: 'delete',
      cron: { expression: '0 0 * * *', timezone: 'UTC' },
    },
  },
  snmp: {
    enabled: false,
    authenticationType: 'SHA-512',
  },
  scanner: {
    enabled: false,
    shortestMask: 20,
  },
  showRoutes: [],
  siteSeparation: {
    manualEnabled: false,
    neighborshipFallbackEnabled: true,
    rules: [
      {
        id: v4(),
        firewall: false,
        minimumSiteSize: 1,
        note: 'routing & switching',
        transformation: 'uppercase',
        type: 'routingSwitching',
        wan: ['serial', 'tunnel'],
      },
    ],
  },
  syslog: {
    lastPull: -1,
    trigger: 'syslogtrigger',
    triggerTime: 60,
    scheduleTime: { expression: '0 0 * * *', timezone: 'UTC' },
  },
  timeouts: {
    cmdTimeout: CLI_DEFAULT_DATA_TIMEOUT,
    readyTimeout: CLI_DEFAULT_LOGIN_TIMEOUT,
    unauthenticatedTimeout: CLI_DEFAULT_UNAUTHENTICATED_TIMEOUT,
    commandResponseTimeoutMinutes: CLI_DEFAULT_COMMAND_RESPONSE_TIMEOUT / 60,
  },
  trace: {
    protocol: 'icmp',
    port: 33434,
    scope: [
      '10.0.0.0/8',
      '100.64.0.0/10',
      '169.254.0.0/16',
      '172.16.0.0/12',
      '192.168.0.0/16',
    ],
  },
  ports: [],
  vendorApi: [],
  dashboard: [],
  aim: {
    integrations: [],
    manual: {
      applications: [],
      flows: [],
      workloads: [],
    },
  },
};
